import type { PropertyValues, TemplateResult } from 'lit'
import type { DroppedQuestions } from './api'
import type { SurveyContent, SurveyResult, SurveySubmission } from './contract'
import type { PulseEmptyDetail, PulseReadyDetail } from './events'
import type { Page } from './paging'
import type { PostSubmitView } from './post-submit'
import type { PrefillSession } from './prefill'
import type { FrameOpts } from './render/frame'
import type { QuestionHandlers } from './render/question'
import type { Phase, Step } from './state'
import { html, LitElement, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { getSurvey, parseResult, parseSurvey, submitSurvey, warnDroppedQuestions, warnDroppedResultQuestions, warnMalformedResult, warnMalformedSurvey } from './api'
import { resolveBrand, resolveHeaderBrand } from './design-system/brands'
import { PALETTES } from './design-system/palettes'
import { submitErrorMessage } from './errors'
import { hostStyles } from './host-styles'
import { observeNearViewport } from './lazy'
import { derivePage } from './paging'
import { overlayVariant, postSubmitOutcome, postSubmitView } from './post-submit'
import { openPrefill } from './prefill'
import { resolveSelectionLimit, selectionCap } from './question-type'
import { activeStyles, activeView } from './render/active'
import { controlStyles } from './render/controls'
import { DropdownOverlay, dropdownStyles } from './render/dropdown'
import { frameStyles, frameView } from './render/frame'
import { questionStyles, questionView } from './render/question'
import { resultsStyles, resultsView, thankYouView } from './render/results'
import { revealFirstInvalid } from './render/reveal'
import { collapsedPromptView, resultsSummaryView, SidebarOverlay, sidebarShellStyles } from './render/sidebar-shell'
import { serveOutcome } from './serve-outcome'
import { buildSteps, buildSubmitResponses, canAdoptMpid, hasAnyAnswer, incompleteQuestionIds } from './state'
import { applyPalette, themeStyles } from './theme'

function hasDisplayableResults(result: SurveyResult | undefined): boolean {
  return (result?.questions.length ?? 0) > 0
}

/**
 * Pulse 問卷嵌入 widget。依 brand/category targeting 由後端選題並渲染；未命中則 no-fill 不顯示。
 *
 * @summary 依 brand/category targeting 由後端選題並渲染的嵌入式問卷元件。
 * @fires pulse:ready - 問卷就緒時：load 成功且至少一題，或 preview 套用 surveyData（來源不拘 fetch / property）。detail: `PulseReadyDetail`（`{ surveyId }`）。
 * @fires pulse:empty - 無問卷可顯示時：no-config（缺 brand/category）/ no-fill（後端無可投放問卷，回 204，或 404）/ no-questions（題目全被過濾）/ error（拿問卷失敗：4xx（404 除外）/ 5xx / 網路錯誤 / malformed）。一律收合不顯示、不彈錯誤卡。detail: `PulseEmptyDetail`（`{ reason }`）。
 * @fires pulse:close - overlay（sidebar / modal / manual）關閉時。無 detail。
 */
export class PulseSurvey extends LitElement {
  static override styles = [
    themeStyles,
    controlStyles,
    frameStyles,
    questionStyles,
    dropdownStyles,
    activeStyles,
    resultsStyles,
    hostStyles,
    sidebarShellStyles,
  ]

  /** 版面：inline 內文嵌入、sidebar 側欄、modal 彈窗、manual 由 JS 開關。 */
  @property() layout: 'inline' | 'sidebar' | 'modal' | 'manual' = 'inline'
  /** Preview 模式：property-fed（surveyData/resultsData），不打 API、不送出。 */
  @property({ type: Boolean }) preview = false
  /** 由 embed 自動掛載時帶上 —— 捲到接近 viewport 才 fetch（首屏省一個請求）。 */
  @property({ type: Boolean }) lazy = false
  /** 讀者身份（mParticle MPID）；宿主顯式帶入或由 sdk-idsync 解析。晚到會觸發未作答問卷重載。 */
  @property({ attribute: 'mpid' }) mpid = ''
  /** IP 品牌：API path 段（GET/POST /v1/surveys/{brand}）＋ palette key。必填。 */
  @property({ attribute: 'brand-id' }) brandId = ''
  /**
   * 文章分類：作為 GET 的 category_id query，pass-through 給後端（widget 不做 category 邏輯）。
   * 選填——缺值代表此版位無分類，收合成 no-config 且不打 API。
   */
  @property({ attribute: 'category-id' }) categoryId = ''

  /** Preview 專用：admin 預先載入的問卷內容，設定後直接渲染，不觸發 fetch。 */
  @property({ attribute: false }) surveyData?: SurveyContent
  // 宣告成 `T | undefined` 而不是 `?: T`：收回這份資料是有意義的操作，而
  // exactOptionalPropertyTypes 下 `?: T` 會讓 consumer 連 `el.resultsData = undefined` 都編不過，
  // 只剩 `delete` 這條路。surveyData 沒有這個需求 —— 換問卷是換一份，不是清空。
  /**
   * Preview 專用：admin 預先計算的作答結果，供送出後的結果畫面顯示。
   * 設回 `undefined` 代表這份問卷不顯示結果，預覽改演致謝畫面。
   */
  @property({ attribute: false }) resultsData: SurveyResult | undefined = undefined

  @state() private phase: Phase = 'loading'
  @state() private survey?: SurveyContent
  @state() private errMsg = ''
  // 按下推進鈕當下驗證未通過的題目 id；使用者一開始修正該題就移除。
  @state() private invalidQIds: string[] = []
  @state() private pageIdx = 0
  // Preview 專用：surveyData 裡沒通過驗證、因而不會渲染的題數。正式路徑的同一個過濾發生在
  // api 層、讀者看不到也不該看到；preview 的觀眾是 admin，要讓他們知道讀者會少看到什麼。
  @state() private previewDropped: DroppedQuestions | null = null
  // 上一次為 preview 說過的那句話。純記帳、不影響渲染，所以不是 @state。
  private warnedDropKey = ''
  // 同上，記的是 resultsData 那條路徑上一次說過的話；與 warnedDropKey 分開，免得兩條路徑互相洗掉。
  private warnedResultDropKey = ''
  // preview 收下並驗過的 resultsData。與 result 分開存，因為 applySurvey 換問卷時會清掉
  // result，而 admin 餵進來的那份還在 —— finish() 要靠這裡把它放回去。
  private previewResult: SurveyResult | undefined = undefined
  @state() private optionAnswers: Record<string, string[]> = {}
  @state() private textAnswers: Record<string, string> = {}
  @state() private pending = false
  @state() private result: SurveyResult | undefined = undefined

  // sidebar 版型的「觸發物 + 底部彈出 overlay」外殼；overlay 開關/showModal/捲動鎖封裝在此 controller。
  // 傳入 close 通知：dialog 任何方式關閉後，對外發 pulse:close（讓整合方還原自家觸發鈕狀態）。
  private sidebarOverlay = new SidebarOverlay(this, () => this.emit('pulse:close'))
  // 自繪 dropdown 的展開狀態與 top-layer 面板（見 render/dropdown.ts）。
  private dropdown = new DropdownOverlay(this)
  // 收合 Q1 用的 wrapped handlers：先套用選擇 / 視為互動，再開 dialog 從 Q1 接續作答。
  // 收合不在卡片內逐字編輯文字 —— textarea/date 一旦互動就開 dialog（在 dialog 內編輯）。
  private collapsedHandlers: QuestionHandlers = {
    toggleOption: (qId, optId, multi) => {
      this.toggleOption(qId, optId, multi)
      this.sidebarOverlay.open()
    },
    setText: () => { this.sidebarOverlay.open() },
    // 收合卡不就地展開選單 —— 點下去等同「開始作答」，改開 dialog 讓使用者在裡面選。
    openDropdown: () => { this.sidebarOverlay.open() },
    closeDropdown: () => {},
    setDropdownActive: () => {},
  }

  private steps: Step[] = []
  private stopObserve?: () => void
  // 上一次 load() 使用的 explicit reader id（mpid）；用來判斷「晚到的 id」是否真的變了。
  private loadedMpid: string | undefined
  // 這次載入的讀者身分與他先前留下的答案（見 prefill.ts 的 PrefillSession）。身分在開場就定了，
  // GET 與送出都讀它，所以 idsync 在作答途中才變 ready 也不會讓同一次作答帶兩個 mp_id。
  // preview 不開 session：那條路不 fetch、沒有讀者、也不寫記憶。
  private prefill?: PrefillSession
  // load() 可由 connect / observer / 晚到 mpid / reconnect 並發觸發。generation 確保
  // last-request-wins（而非 last-response-wins）：每次 load 自成一代，await 回來若已非最新代就放棄套用；
  // loadAbort 取消上一個進行中的請求（含 disconnect 時）。
  private loadGeneration = 0
  private loadAbort?: AbortController
  // 這位讀者的票已經投出去了（POST 拿到 201 或 410）。一票投完就不再拿新問卷 —— 見 load() 的守門。
  // 送出失敗（網路 / 4xx / 5xx）不算：那張票沒進去，讀者還要能重試，所以只在定案的兩種結局設。
  // 這個閂跟著元素實例走，而且不會再打開：換投放對象或換讀者要重建元素（brand-id /
  // category-id 本來就是 connect-time immutable，見 willUpdate），把同一個元素當版位回收再用
  // 會得到一個安靜的空版位 —— 不發事件、不打請求。
  private submitted = false
  // modal 版型只在內容首次就緒時自動跳出一次；之後使用者關閉就不再自動彈（避免騷擾）。
  private modalAutoOpened = false

  override connectedCallback(): void {
    super.connectedCallback()
    // !lazy（顯式放置或非延遲掛載）立即 fetch；lazy（自動掛載）延後到接近 viewport 才 fetch。
    // preview 本質是 property-fed、必走 !lazy path。manual 無頁面版位可捲 → 一律 eager 載入，
    // 讓內容早就緒、並能在觸發前就發 pulse:ready / pulse:empty 供整合方判斷要不要顯示觸發鈕。
    if (this.preview || !this.lazy || this.layout === 'manual') {
      void this.load()
      return
    }
    this.stopObserve = observeNearViewport(this, () => void this.load())
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.stopObserve?.()
    // 取消進行中的 load：避免元件已移除、舊請求才回來時還去改 phase。重連會再 load()。
    this.loadAbort?.abort()
  }

  // modal 版型：內容首次就緒（survey 已載入、非 loading/empty）時自動跳出 overlay 一次。
  // 之後使用者關閉就不再自動彈 —— modalAutoOpened 旗標確保只觸發一次（open 本身 idempotent）。
  override updated(): void {
    if (
      this.layout === 'modal'
      && !this.modalAutoOpened
      && this.survey
      && this.phase !== 'loading'
      && this.phase !== 'empty'
    ) {
      this.modalAutoOpened = true
      this.sidebarOverlay.open()
    }
  }

  // Reactive input 契約：
  // - preview 是 property-fed：surveyData / resultsData 由 admin 在 connect 後設定或抽換，變更即重套用。
  // - mpid：runtime reactive，晚到且身分變了時重載（見下）。
  // - brand-id / category-id：connect-time immutable config。宿主要改投放對象請重建元素。
  override willUpdate(changed: PropertyValues<this>): void {
    if (this.preview) {
      if (changed.has('surveyData') && this.surveyData)
        this.adoptPreviewSurvey(this.surveyData)
      if (changed.has('resultsData'))
        this.adoptPreviewResult(this.resultsData)
      return
    }
    // 承接晚到的 mpid：只有在「已 load 過(loadedMpid 已設) 且顯式 id 變成
    // 與當初載入時不同的非空值」時才重發 —— 避免顯式 id 在 connect 前就設好時，首次 render 又多打一次。
    // 已作答 / 已送出則不換身分（canAdoptMpid）：避免打斷讀者已開始的作答。
    if (!changed.has('mpid'))
      return
    const next = this.mpid
    if (
      this.loadedMpid !== undefined
      && next !== this.loadedMpid
      && next
      && canAdoptMpid(this.phase, this.optionAnswers, this.textAnswers)
    ) {
      void this.load()
    }
  }

  private async load(): Promise<void> {
    // Preview is property-fed (admin sets .surveyData): no fetch, no mpid context. willUpdate
    // owns applying surveyData — both the initial value and any late / replaced one.
    if (this.preview)
      return
    // 一位讀者一票：票在路上或已經投出去之後，這個元素就不再拿問卷。少了這道門，reconnect
    // （SPA 換版位、廣告插槽搬移、lazy 版位重新進入視窗）與晚到的 mpid 都會把 phase 拉回 active，
    // 已投過票的讀者被重新要求作答 —— 而第二次送出後端只會回 409（「您已經填過這份問卷了」）
    // 或 410，兩種都是讀者白填一次。
    //
    // 擋在 load() 而不是 connectedCallback：載入的觸發點不只一個（`rg 'this\.load\(\)' src`
    // 找得出當下全部），每一個都通往同一件事，規則只該寫這一次。已收下的答案與畫面都留在
    // 元素上，所以被搬走再接回來的讀者看到的仍是自己那份結果 / 已收合的版位。
    if (this.pending || this.submitted)
      return
    // 開新一代：中止上一個進行中的請求，並記下本代序號。await 回來後比對 generation，
    // 若已被更新的 load 取代就直接放棄，不得覆寫新狀態。
    this.loadAbort?.abort()
    const abort = new AbortController()
    this.loadAbort = abort
    const generation = ++this.loadGeneration
    const isStale = (): boolean => generation !== this.loadGeneration

    // Clear a prior no-fill collapse (e.g. a late mpid triggers a reload of an 'empty' slot) —
    // here rather than on arrival, so a reloading slot shows its loading state instead of staying
    // collapsed until the response lands.
    this.style.removeProperty('display')
    this.phase = 'loading'
    // No serve target: nothing to ask for, so the answer is known without asking.
    if (!this.brandId || !this.categoryId) {
      this.collapse('no-config')
      return
    }
    // mpid is the explicit (host) reader id. resolveMpid cascades host > sdk, else falls back to
    // an empty id; record the explicit input to gate reloads.
    const explicit = this.mpid
    this.loadedMpid = explicit
    // 身分在這裡解析一次；之後 GET、seed 與送出讀的都是 `this.prefill.mpid`
    // （`rg 'resolveMpid' src/` 看得出有沒有第二處在解析）。
    this.prefill = openPrefill(explicit)
    const result = await getSurvey(this.brandId, { mpId: this.prefill.mpid.id, category: this.categoryId }, abort.signal)
    if (isStale())
      return
    const outcome = serveOutcome(result)
    if (outcome.kind === 'empty') {
      this.collapse(outcome.reason)
      return
    }
    this.adoptSurvey(outcome.survey, 'live')
  }

  // The one place a serve outcome takes the slot off the page. Every reason collapses identically
  // — no error card, no retry button, ever — and an integrator tells the cases apart by the reason
  // on pulse:empty.
  //
  // finishDismiss() hides the host with the same two lines and deliberately does not come through
  // here: a reader dismissing a finished survey is not an absence of one, so it stays silent. Any
  // change to what collapsing *means* has to be made in both places on purpose.
  private collapse(reason: PulseEmptyDetail['reason']): void {
    this.phase = 'empty'
    this.style.display = 'none'
    this.emit('pulse:empty', { reason } satisfies PulseEmptyDetail)
  }

  // The one place a survey becomes the thing on screen, whether it was fetched or handed over as a
  // property. Two things differ by source once it is in hand, and both are here:
  //
  //   prefill seed — live only. Saved answers are keyed by reader id, and preview has no reader.
  //   pulse:ready  — live emits on every load, including the reload a late mpid triggers, because
  //                  an integrator watching for "there is something to show" needs to hear it each
  //                  time a slot fills. Preview emits only when the survey identity changes, so an
  //                  admin re-applying the same survey (a wording or theme tweak) does not restate
  //                  readiness on every keystroke.
  //
  // Preview diverges elsewhere too — it is always eager, never fetches, never writes prefill,
  // never POSTs — so this method is not the whole story about preview, only about what happens
  // once a survey is in hand. `rg 'this.preview'` finds every branch, which is a more reliable
  // answer than a list here that a later change can quietly outgrow.
  //
  // Preview 走與正式路徑同一支驗證。少了它，預覽會顯示讀者根本看不到的東西：聯集外的題型被
  // 當成單選題畫出來，而選項不是陣列的題目讓 render 在 `.map` 上拋 TypeError —— 兩者在正式
  // 路徑都是「這題不存在」。會顯示正式環境不會出現的題目的預覽，是個會說謊的預覽。
  //
  // 與正式路徑唯一刻意的差異是 thumbnail：preview 額外收 raster 的 data:image，讓使用者選了封面、
  // 還沒儲存時預覽就看得到（理由與邊界見 api.ts 的 isHttpsUrl 註解）；其餘驗證完全相同。
  private adoptPreviewSurvey(data: SurveyContent): void {
    const parsed = parseSurvey(data, { allowDataImageThumbnail: true })
    if (!parsed) {
      // top-level 結構壞掉：與正式路徑一致收合。這裡沒有畫面可以掛提示（整份都沒收下），
      // 所以 warn 是唯一的說明。
      this.previewDropped = null
      warnMalformedSurvey()
      this.collapse('error')
      return
    }
    // 題目全被濾掉＝讀者那邊整個版位不存在（正式路徑走 no-questions 收合）。這裡跟著收合，
    // 否則 admin 會看到一個空殼問卷、以為只是還沒加題目。
    if (parsed.survey.questions.length === 0) {
      this.previewDropped = null
      warnDroppedQuestions(parsed.survey.id, parsed.dropped)
      this.collapse('no-questions')
      return
    }
    this.previewDropped = parsed.dropped.total > 0 ? parsed.dropped : null
    // preview 的同一份問卷會被反覆套用（admin 每改一個字就重新指派 surveyData），所以只在
    // 「說的話會不一樣」時才說。
    const key = `${parsed.survey.id}:${parsed.dropped.total}:${parsed.dropped.required}`
    if (parsed.dropped.total > 0 && key !== this.warnedDropKey) {
      this.warnedDropKey = key
      warnDroppedQuestions(parsed.survey.id, parsed.dropped)
    }
    this.adoptSurvey(parsed.survey, 'preview')
  }

  // admin 餵進來的 result 走與正式路徑同一支 parseResult。送出那側整份讀不動的 result 是降級成
  // 致謝畫面＋warn（見 submitSurvey），preview 跟著一致：不驗的話 resultsView 會在 questions.map
  // 上拋 TypeError，演出一個正式環境不會出現的畫面。讀得動的題照樣顯示、其餘逐題丟掉，一題都不剩
  // 就當作沒有結果（致謝畫面），同樣與送出那側一致。
  private adoptPreviewResult(data: SurveyResult | undefined): void {
    const parsed = parseResult(data)
    if (parsed === null) {
      // 收回 resultsData（admin 把「顯示結果」關掉）與餵進一份整份讀不動的東西，都落在 parsed
      // === null，但只有後者要說話：前者是 admin 說的，不是壞掉。
      if (data !== undefined)
        warnMalformedResult()
      this.previewResult = undefined
      this.result = undefined
      return
    }
    // 同一份 resultsData 會被 admin 反覆指派（每個 keystroke 一次），所以只在「說的話會不一樣」
    // 時才說。一份讀得動的結果會清掉記帳：之後同樣的壞題再出現，是一件新的事。
    const key = parsed.dropped.total > 0 ? `${parsed.result.id}:${parsed.dropped.total}:${parsed.dropped.seqs.join(',')}` : ''
    if (key !== '' && key !== this.warnedResultDropKey)
      warnDroppedResultQuestions(parsed.result.id, parsed.dropped)
    this.warnedResultDropKey = key
    const accepted = parsed.result.questions.length > 0 ? parsed.result : undefined
    this.previewResult = accepted
    this.result = accepted
  }

  // Un-collapsing here as well as at load() start covers the pre-upgrade property-fed mount: the
  // widget script arrives late, Lit restores the pre-construction properties only on first update,
  // so connectedCallback ran load() with preview still false and no brand-id and already collapsed
  // the host as no-config.
  private adoptSurvey(survey: SurveyContent, source: 'live' | 'preview'): void {
    const isNewSurvey = this.survey?.id !== survey.id
    this.style.removeProperty('display')
    this.applySurvey(survey)
    if (source === 'live' && this.prefill) {
      // 首次載入時 applySurvey 剛把兩張 map 清空，所以這一步就是單純的 seed；同一份問卷被重載
      // （DOM re-parenting 會重跑 load()）時，讀者填到一半、還沒送出的答案會勝出。哪一邊贏是
      // seed() 的規則，不是這裡的選擇。
      const seeded = this.prefill.seed(survey, { optionAnswers: this.optionAnswers, textAnswers: this.textAnswers })
      this.optionAnswers = seeded.optionAnswers
      this.textAnswers = seeded.textAnswers
    }
    // 相信 API：廣告投放模型下能回問卷就是可投放/未過期，widget 只負責呈現、不自算是否過期
    // （過期由 API 判斷：serve 未命中 → 404 no-fill；作答時已結束 → submit 回 410 → ended）。
    this.phase = 'active'
    if (source === 'live' || isNewSurvey)
      this.emit('pulse:ready', { surveyId: survey.id } satisfies PulseReadyDetail)
  }

  // Session boundary: apply a loaded survey (live or preview) to element state — survey +
  // derived steps + theme — and atomically reset the in-progress session (current page /
  // option+text answers / result) whenever the survey's IDENTITY changes (a different
  // survey.id, or the first-ever application). Without this, a stale answer keyed by a
  // question id that happens to recur in the new survey (e.g. two surveys both reusing
  // id "q1") would silently carry over — the bug this boundary exists to close.
  //
  // Reapplying the SAME survey.id intentionally SKIPS the reset: preview live-reloads
  // (admin tweaks title/theme/wording, surveyData gets a new object reference but the same
  // id) must not interrupt a reader mid-questionnaire by wiping their in-progress answers.
  // Identity is taken to be survey.id alone (the backend's stable survey key); a same-id
  // reapply is treated as the SAME questionnaire, so a hypothetical "same id, different
  // questions" payload would not re-reset — an accepted trade-off given the backend never
  // reuses an id for a different survey.
  private applySurvey(survey: SurveyContent): void {
    const isNewSurvey = this.survey?.id !== survey.id
    this.survey = survey
    this.steps = buildSteps(survey)
    applyPalette(this, PALETTES[resolveBrand(this.brandId)])
    if (isNewSurvey) {
      this.pageIdx = 0
      this.optionAnswers = {}
      this.textAnswers = {}
      this.result = undefined
      this.errMsg = ''
      this.invalidQIds = []
    }
  }

  private toggleOption = (qId: string, optId: string, multi: boolean): void => {
    const cur = this.optionAnswers[qId] ?? []
    let next: string[]
    if (!multi) {
      next = [optId]
    }
    else if (cur.includes(optId)) {
      next = cur.filter(x => x !== optId)
    }
    else {
      // 上限已滿（at_most／exactly 勾到 count）：未選中的其他 option 不得加入
      // （已選中的仍可透過上面分支取消）。
      const q = this.steps.find(s => String(s.q.id) === qId)?.q
      const cap = q ? selectionCap(resolveSelectionLimit(q)) : null
      if (cap !== null && cur.length >= cap)
        return
      next = [...cur, optId]
    }
    this.optionAnswers = { ...this.optionAnswers, [qId]: next }
    this.clearHints(qId)
    this.rememberAnswer(qId)
  }

  private setText = (qId: string, value: string): void => {
    this.textAnswers = { ...this.textAnswers, [qId]: value }
    this.clearHints(qId)
    this.rememberAnswer(qId)
  }

  // 一有動作就撤掉該題的標記與 action bar 上方的提示，紅字不會跟著使用者的修正繼續留在畫面上。
  // 不重新驗證：修到一半（如 date 只打了年）不該立刻再被判失敗，下次按推進鈕會重新驗。
  // errMsg 同理一併清掉 —— 它承載的兩則訊息（送出失敗、整份零作答）都描述「上一次按下推進鈕
  // 當下」的狀態，使用者一改答案就不再成立，下次按下去會重新算出該說什麼。
  private clearHints(qId: string): void {
    if (this.invalidQIds.includes(qId))
      this.invalidQIds = this.invalidQIds.filter(id => id !== qId)
    this.errMsg = ''
  }

  // 作答當下把答案交給這次載入的記憶（見 prefill.ts）。哪些題記得住、匿名讀者怎麼辦，
  // 都由 remember() 回答；preview 沒有 session，這裡自然是 no-op。
  private rememberAnswer(qId: string): void {
    const q = this.steps.find(s => String(s.q.id) === qId)?.q
    if (q)
      this.prefill?.remember(q, { optionAnswers: this.optionAnswers, textAnswers: this.textAnswers })
  }

  // overlay 版型（sidebar / modal / manual）共用收合插槽 + dialog 外殼；inline 直接 in-flow 渲染。
  private isOverlay(): boolean {
    return this.layout === 'sidebar' || this.layout === 'modal' || this.layout === 'manual'
  }

  // 當前頁 —— 作答畫面、推進鈕的驗證範圍、頁碼與進度條全部由這一個 accessor 供應。
  // 純函式重算而不快取成欄位：steps / pageIdx / layout / survey 任一變動都會影響結果，
  // 快取就得自己記得在四個地方失效，那正是這裡要消滅的那種「靠人記得」。
  private page(): Page {
    return derivePage({
      steps: this.steps,
      pageIdx: this.pageIdx,
      questionsPerPage: this.survey?.questions_per_page,
      isOverlay: this.isOverlay(),
    })
  }

  // manual 是 headless：觸發前不顯示任何收合內容（不佔版面）；其餘 overlay 版型維持各自的收合卡。
  private collapsedFor(body: TemplateResult): TemplateResult {
    return this.layout === 'manual' ? html`` : body
  }

  // 對外事件：bubbles 讓宿主祖先 listener 收得到（this 本身即在宿主 light DOM，無需 composed）。
  private emit(type: string, detail?: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }))
  }

  // 公開命令式 API：整合方（IP 站）以自家按鈕 / 捲動邏輯決定何時彈出問卷。
  // 只對 overlay 版型有意義；inline 無 overlay → no-op + 警告。
  open(): void {
    if (!this.isOverlay()) {
      console.warn('[Pulse] open() is only supported on overlay layouts (sidebar/modal/manual); ignored for inline.')
      return
    }
    // 尚未載入的 lazy 插槽（還沒捲進視窗）—— 由 open() 觸發載入。manual 於 connect 已載入（generation≥1）。
    if (this.loadGeneration === 0 && !this.preview) {
      this.stopObserve?.()
      void this.load()
    }
    // no-fill 無內容可彈。
    if (this.phase === 'empty')
      return
    this.sidebarOverlay.open()
  }

  close(): void {
    if (!this.isOverlay())
      return
    this.sidebarOverlay.close()
  }

  // 推進鈕一律可按 —— 這一頁沒答完就不前進，改成標出缺哪幾題並捲到第一個。
  // （鈕鎖住不說原因的話，使用者只能自己猜哪裡沒填。）
  private next = (): void => {
    const page = this.page()
    const invalid = incompleteQuestionIds(page.steps, this.optionAnswers, this.textAnswers)
    if (invalid.length > 0) {
      this.invalidQIds = invalid
      this.scrollToFirstInvalid()
      return
    }
    this.invalidQIds = []
    if (!page.isLast) {
      this.pageIdx += 1
      return
    }
    // 送出的下限：整份問卷至少答一題。全部題目都選填時，上面的逐題驗證會全部放行，
    // 使用者一路按到底就送出一筆空回應 —— 這裡是唯一擋得住它的地方。
    // 不標紅任何一題：選填題沒有哪一題「錯了」，缺的是整份的量，所以改用 action bar
    // 上方的 hint 說明原因（標紅會誤導成「這題必填」）。
    if (this.survey && !hasAnyAnswer(this.survey, this.optionAnswers, this.textAnswers)) {
      this.errMsg = '請至少回答一題後再送出'
      this.scrollToHint()
      return
    }
    void this.finish()
  }

  private back = (): void => {
    if (this.pageIdx > 0) {
      this.invalidQIds = []
      this.pageIdx -= 1
    }
  }

  // hint 排在題目之後、sticky action bar 之前 —— 題目長到要捲動時，按下推進鈕的當下它在
  // 視窗外，畫面看起來像沒反應。捲過去才看得到為什麼沒送出。
  private scrollToHint(): void {
    void this.updateComplete.then(() => {
      this.renderRoot.querySelector<HTMLElement>('.form-hint')?.scrollIntoView?.({ block: 'nearest' })
    })
  }

  // 錨點由 activeView 掛在 .page-step 上、錯誤說明要等這一輪渲染完才存在。捲動到哪裡的規則
  // （標題與錯誤同時可見、只動 widget 內部的捲動容器）在 render/reveal.ts。
  private scrollToFirstInvalid(): void {
    void this.updateComplete.then(() => revealFirstInvalid(this.renderRoot))
  }

  // 多頁時的頁碼（current / total），渲染進 frame 的 progress bar；單頁回 null（不顯示）。
  private activePageInfo(): { current: number, total: number } | null {
    const { pageIdx, pageCount } = this.page()
    return pageCount <= 1 ? null : { current: pageIdx + 1, total: pageCount }
  }

  // 頁位置進度：對齊頁碼 —— 第 1 頁 = 1/N、最後一頁滿（fill = (pageIdx+1)/pageCount）；
  // 單頁無「進度」可言→回 null 不顯示。換頁才前進（作答當前頁不移動）。
  private pageProgress(): number | null {
    const { pageIdx, pageCount } = this.page()
    return pageCount <= 1 ? null : Math.round(((pageIdx + 1) / pageCount) * 100)
  }

  // 打開結果 popup。overlay 版型送出後 dialog 本就開著（idempotent no-op）；inline 送出後改走 overlay
  // 殼、由此開啟；也供收合槽的「看結果」鈕重開已關閉的 popup（bound arrow 才能當 handler 傳出）。
  private openResultsPopup = (): void => { this.sidebarOverlay.open() }

  // 送出／結束之後自動把 popup 打開。哪些格子有 popup 由 post-submit 的表回答 ——
  // in-flow 那格沒有殼可開，所以什麼都不做。
  private openResultsAfterSubmit(): void {
    if (this.postSubmit().shell === 'overlay')
      this.openResultsPopup()
  }

  // 當前這一格。phase 與 result 是它的唯二輸入，兩者都是 @state，所以純函式重算不必失效。
  private postSubmit(): PostSubmitView {
    return postSubmitView(this.layout, postSubmitOutcome(this.phase === 'ended', hasDisplayableResults(this.result)))
  }

  // 結果 popup 的「結束」：僅關 popup（inline / modal / manual / sidebar 有結果）。
  private closePopup = (): void => { this.sidebarOverlay.close() }

  // sidebar 無結果的「結束」：關 popup 並讓 widget 消失（沿用 empty 收合手法 phase=empty + display:none）。
  // 必須先 close()（dialog 仍在 DOM 時才收得到 close 事件、還原捲動鎖）再切 phase，否則 render 先移除 dialog
  // 就漏掉解鎖。
  private finishDismiss = (): void => {
    this.sidebarOverlay.close()
    this.phase = 'empty'
    this.style.display = 'none'
  }

  private async finish(): Promise<void> {
    const survey = this.survey
    if (!survey)
      return
    // Preview never POSTs — go straight to results with the handed-in result, or the empty-state
    // results view when none was provided.
    if (this.preview) {
      this.result = this.previewResult
      this.phase = 'results'
      this.openResultsAfterSubmit()
      return
    }
    this.pending = true
    this.errMsg = ''
    try {
      // 沿用 load() 當下解析的身分，idsync 在中途變 ready 也不影響。沒有 session 就沒有身分
      // 可帶（送出時省略 mp_id）—— 那代表沒載入過問卷，也就沒有東西送得出去。
      const mpId = this.prefill?.mpid.id ?? ''
      const body: SurveySubmission = {
        survey_id: survey.id,
        response: buildSubmitResponses(survey, this.optionAnswers, this.textAnswers),
        ...(mpId ? { mp_id: mpId } : {}),
      }
      const out = await submitSurvey(this.brandId, body)
      // 閂只問一件事：這位讀者對這份問卷還能不能投。三種結局都回答「不能」——201（票收下了）、
      // 410（問卷不再收票）、409（後端說這位讀者已經填過，`duplicate`）。409 排在這裡而不是跟
      // 其他失敗一起，是因為它跟 400 / 5xx / 網路錯誤不同類：那些的票沒進去、讀者按重試時還需要
      // 現在這份問卷，而 409 再按幾次都只會拿回同一句話。
      const settled = out.kind === 'submitted' || out.kind === 'ended' || (out.kind === 'failed' && out.reason === 'duplicate')
      if (settled)
        this.submitted = true
      if (out.kind === 'submitted') {
        // 條件式回應：顯示結果的問卷回聚合統計 → 結果頁；不顯示結果 → result undefined → 致謝畫面
        // （resultsView 的 !result 分支）。兩者都進 results phase，送出即定案不再另打 API。
        this.result = out.result
        this.phase = 'results'
        this.openResultsAfterSubmit()
        return
      }
      if (out.kind === 'ended') {
        this.phase = 'ended'
        this.openResultsAfterSubmit()
        return
      }
      this.errMsg = submitErrorMessage(out.reason)
    }
    finally {
      this.pending = false
    }
  }

  // 給 admin 的診斷訊息，不是產品 UI —— 所以沿用 .form-hint 的視覺（activeStyles 裡兩者共用
  // 同一條規則），但**不能共用 class**：scrollToHint() 用 querySelector('.form-hint') 抓送出
  // 提示，而這行排在題目之前，共用的話 preview 下會捲到錯的那一個。
  // 只在 preview 渲染：正式路徑的讀者不該看到後端資料問題的說明。
  private previewNotice(): TemplateResult | typeof nothing {
    const d = this.previewDropped
    if (!this.preview || !d)
      return nothing
    const required = d.required > 0 ? `，其中 ${d.required} 題為必填` : ''
    return html`<p class="preview-notice">預覽提醒：有 ${d.total} 題因資料不符合格式而不會顯示給讀者${required}。</p>`
  }

  private activeBody(): TemplateResult {
    return activeView(
      {
        page: this.page(),
        optionAnswers: this.optionAnswers,
        textAnswers: this.textAnswers,
        pending: this.pending,
        errMsg: this.errMsg,
        progress: this.pageProgress(),
        invalidQIds: this.invalidQIds,
        notice: this.previewNotice(),
        openDropdownQId: this.dropdown.openQId,
        dropdownActiveIndex: this.dropdown.activeIndex,
      },
      {
        toggleOption: this.toggleOption,
        setText: this.setText,
        openDropdown: this.dropdown.open,
        closeDropdown: this.dropdown.close,
        setDropdownActive: this.dropdown.setActive,
        back: this.back,
        next: this.next,
      },
    )
  }

  // sidebar/overlay 作答中的收合內容：真實 Q1（可互動、互動即開 dialog）；無題目時退回完整 frame。
  // 只在 active phase 呼叫（送出後的收合內容由 resultsRender 產生）。
  private sidebarCollapsed(frameOpts: FrameOpts): TemplateResult {
    const step0 = this.steps[0]
    if (step0) {
      const q1 = questionView(
        step0,
        { optionAnswers: this.optionAnswers, textAnswers: this.textAnswers },
        this.collapsedHandlers,
      )
      // 收合卡（Figma 314:3461）：hero 只留 badge+標題（無描述）、無進度條、
      // Q1 + 底部「查看所有選項」CTA；dialog 內才有完整 chrome。
      return frameView({ ...frameOpts, description: null, progress: null, pageInfo: null }, collapsedPromptView(q1))
    }
    // 無題目時退回完整 frame、避免收合空白。
    return frameView(frameOpts, this.activeBody())
  }

  // 已填的精簡摘要卡（含問卷標題）。依 phase 取狀態行 + CTA、
  // 避免對「問卷已結束但本人未作答」誤稱「已完成」。
  private collapsedSummary(title: string): TemplateResult {
    if (this.phase === 'ended')
      return resultsSummaryView({ title, statusLine: '問卷已結束', ctaLabel: '看結果 →' })
    return resultsSummaryView({ title, statusLine: '✓ 已完成作答', ctaLabel: '看完整結果 →' })
  }

  // 送出後（results / ended）畫面的 frame chrome：無進度條/頁碼；inline 純文字 header、overlay 形式帶 Hero。
  private resultsFrameOpts(s: SurveyContent, inline: boolean): FrameOpts {
    return {
      brand: resolveHeaderBrand(this.brandId, s.does_show_logo),
      title: s.title,
      description: s.description,
      expiresAt: s.expired_at,
      imageUrl: inline ? null : (s.thumbnail || null),
      inline,
      progress: null,
      pageInfo: null,
      view: 'results',
    }
  }

  // 送出後的呈現。哪一格長怎樣寫在 post-submit.ts 的表裡（含兩格與直覺不同的說明）；
  // 這裡只負責把那一格的四個決定翻成畫面。
  private resultsRender(s: SurveyContent): TemplateResult {
    const ended = this.phase === 'ended'
    const view = this.postSubmit()

    // in-flow：不走 overlay 殼，原地渲染結束畫面（無鈕）。
    if (view.shell === 'in-flow')
      return frameView({ ...this.resultsFrameOpts(s, true), view: 'thank-you' }, resultsView(this.result, { description: s.description }))

    // popup 裡放什麼由表說了算，不是由 result 在不在手上決定 —— 兩個地方各判一次的話，它們
    // 會在「表說致謝、手上卻有統計」時各說各話。這樣寫的話那種情況退回致謝殼，而不是端出一頁
    // 沒有任何說明的統計。
    const popupResult = view.popup === 'results' ? this.result : undefined
    const popup = frameView({ ...this.resultsFrameOpts(s, false), view: view.popup }, resultsView(popupResult, {
      ended,
      description: s.description,
      onClose: view.onFinish === 'dismiss-widget' ? this.finishDismiss : this.closePopup,
    }))

    return this.sidebarOverlay.render(this.collapsedBody(view.collapsed, s), popup, overlayVariant(this.layout))
  }

  // 收合槽的三種內容。'thank-you' 帶不帶「看結果」鈕跟著 popup 走 —— popup 是結果頁才有東西可回頭看。
  private collapsedBody(kind: 'summary' | 'thank-you' | 'none', s: SurveyContent): TemplateResult {
    if (kind === 'none')
      return html``
    if (kind === 'summary')
      return this.collapsedSummary(s.title)
    return frameView(
      { ...this.resultsFrameOpts(s, this.layout === 'inline'), view: 'thank-you' },
      thankYouView(hasDisplayableResults(this.result) ? { onViewResult: this.openResultsPopup } : {}),
    )
  }

  override render() {
    // 404 no-fill: host is collapsed (display:none) — render nothing, never the error card.
    if (this.phase === 'empty')
      return html``
    if (this.phase === 'loading') {
      const loadingCard = html`<div class="loading"><p class="muted">載入中…</p></div>`
      if (!this.isOverlay())
        return loadingCard
      // 收合顯示精簡占位卡（可點召喚）；點開後 overlay 內才顯示載入卡片。
      const placeholder = resultsSummaryView({ title: '問卷', statusLine: '載入中…', ctaLabel: '展開 →' })
      return this.sidebarOverlay.render(this.collapsedFor(placeholder), loadingCard, overlayVariant(this.layout))
    }

    const s = this.survey
    // 防禦性：active/results/ended 正常都有 survey；拿問卷失敗已在 load() 收合成 empty（上面已 return）。
    // 若非預期地在無 survey 下走到這裡，比照收合不顯示，絕不彈錯誤卡。
    if (!s)
      return html``

    // 送出後（results / ended）：依版型 × 有無結果分派（見 resultsRender）。
    if (this.phase === 'results' || this.phase === 'ended')
      return this.resultsRender(s)

    // 作答中（active）：inline 純文字 header（Hero 僅 overlay 形式），帶進度條 + 頁碼。
    const inline = this.layout === 'inline'
    const frameOpts: FrameOpts = {
      brand: resolveHeaderBrand(this.brandId, s.does_show_logo),
      title: s.title,
      description: s.description,
      expiresAt: s.expired_at,
      imageUrl: inline ? null : (s.thumbnail || null),
      inline,
      // 統計期間只在作答頁帶進來；結果頁那側讀 SurveyResult 自己的必填欄位（見 results.ts）。
      statsFrom: s.stats_from,
      statsTo: s.stats_to,
      progress: this.pageProgress(),
      pageInfo: this.activePageInfo(),
      view: 'answering',
    }
    const frame = frameView(frameOpts, this.activeBody())
    if (!this.isOverlay())
      return frame
    return this.sidebarOverlay.render(this.collapsedFor(this.sidebarCollapsed(frameOpts)), frame, overlayVariant(this.layout))
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pulse-survey': PulseSurvey
  }
}
